'use strict';

import React, {
    useCallback,
    useLayoutEffect,
    useState
} from 'react';
import {
    useDispatch,
    useSelector
} from 'react-redux';
import _ from 'lodash';
import {keys} from 'ts-transformer-keys';

import {
    Dialog,
    DialogProps
} from 'primereact/dialog';
import {
    TabPanel,
    TabView,
    TabViewTabChangeEvent
} from 'primereact/tabview';

import {__} from '~/shared/src/utilities/messageCatalog';

import {ComponentProps as DataTableComponentProps} from '~/frontend/src/components/base/DataTable';
import ItemHistory from '~/frontend/src/components/base/ItemFinder/history/itemHistory';
import PropertiesHistory from '~/frontend/src/components/base/ItemFinder/history/propertiesHistory';
import ReferencesHistory from '~/frontend/src/components/base/ItemFinder/history/referencesHistory';
import ReferencePropertiesHistory from '~/frontend/src/components/base/ItemFinder/history/referencePropertiesHistory';

import {getSubPanelProps} from '~/frontend/src/components/base/page/PageTabPanel/utilities';

import {
    AppDispatch,
    StoreState
} from '~/frontend/src/store';
import setParam from '~/frontend/src/actions/panel/common/setParam';


const dialogProps = keys<DialogProps>();


export type DefaultProps = {};
export const defaultProps : Required<DefaultProps> = {};

export type Props = {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    schema : Schema;

    rootItemId : number;
};

export type ComponentProps = DialogProps & DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

type AllProps = DialogProps & Required<DefaultProps> & Props;

const ItemHistoryDialog = ( props : AllProps ) => {
    const dispatch : AppDispatch                     = useDispatch(),
          {
              rootItemId,
              schema,
              uid
          }                                          = props,
          [ activeTabIndex, setActiveTabIndex ]      = useState<number>( 0 ),
          onTabViewTabChange                         = useCallback(
              ( event : TabViewTabChangeEvent ) => {
                  setActiveTabIndex( event.index );
              },
              [
                  'onlyOnce'
              ]
          ),
          itemUid                                    = `${uid}_history-item`,
          itemDataTableComponentProps                = useSelector( ( state : StoreState ) => getSubPanelProps( itemUid, state ) as DataTableComponentProps | undefined ),
          propertiesUid                              = `${uid}_history-properties`,
          propertiesDataTableComponentProps          = useSelector( ( state : StoreState ) => getSubPanelProps( propertiesUid, state ) as DataTableComponentProps | undefined ),
          referencesUid                              = `${uid}_history-references`,
          referencesDataTableComponentProps          = useSelector( ( state : StoreState ) => getSubPanelProps( referencesUid, state ) as DataTableComponentProps | undefined ),
          referencePropertiesUid                     = `${uid}_history-referenceProperties`,
          referencePropertiesDataTableComponentProps = useSelector( ( state : StoreState ) => getSubPanelProps( referencePropertiesUid, state ) as DataTableComponentProps | undefined );

    useLayoutEffect(
        () => {
            return () => {
                if ( !props.visible ) {
                    setActiveTabIndex( 0 );
                    dispatch( setParam( uid, 'subPanelProps', undefined ) );
                }
            }
        },
        [
            props.visible
        ]
    );

    return <Dialog
        {..._.pick( props, dialogProps ) as DialogProps}
        header={__( 'YN.base.frontend.dialog.itemHistory.title' )}
        closable={true}
        className="yn-flex-layout-parent"
        contentClassName="yn-flex-layout-parent"
        maskClassName="yn-dialog-panel-modal"
        appendTo="self"
        style={{ width: '80%', maxWidth: '80%', minWidth: '80%', height: '90%' }}
    >
        <TabView
            activeIndex={activeTabIndex}
            onTabChange={onTabViewTabChange}
            className="yn-flex-layout-parent"
        >
            <TabPanel
                key="item"
                header={__( 'YN.base.frontend.dialog.itemHistory.item.header' )}
                contentClassName="yn-flex-layout-parent"
            >
                <ItemHistory
                    {...itemDataTableComponentProps}

                    key={itemUid}

                    uid={itemUid}

                    schema={schema}
                    rootItemId={rootItemId}
                    forceFit={true}
                />
            </TabPanel>
            <TabPanel
                key="properties"
                header={__( 'YN.base.frontend.dialog.itemHistory.properties.header' )}
                contentClassName="yn-flex-layout-parent"
            >
                <PropertiesHistory
                    {...propertiesDataTableComponentProps}

                    key={propertiesUid}

                    uid={propertiesUid}

                    schema={schema}
                    rootItemId={rootItemId}
                    forceFit={true}
                />
            </TabPanel>
            <TabPanel
                key="references"
                header={__( 'YN.base.frontend.dialog.itemHistory.references.header' )}
                contentClassName="yn-flex-layout-parent"
            >
                <ReferencesHistory
                    {...referencesDataTableComponentProps}

                    key={referencesUid}

                    uid={referencesUid}

                    schema={schema}
                    rootItemId={rootItemId}
                    forceFit={true}
                />
            </TabPanel>
            <TabPanel
                key="referenceProperties"
                header={__( 'YN.base.frontend.dialog.itemHistory.referenceProperties.header' )}
                contentClassName="yn-flex-layout-parent"
            >
                <ReferencePropertiesHistory
                    {...referencePropertiesDataTableComponentProps}

                    key={referencePropertiesUid}

                    uid={referencePropertiesUid}

                    schema={schema}
                    rootItemId={rootItemId}
                    forceFit={true}
                />
            </TabPanel>
        </TabView>
    </Dialog>;
};
ItemHistoryDialog.defaultProps = defaultProps;
ItemHistoryDialog.displayName = 'ItemHistoryDialog';

export default ItemHistoryDialog;
